import React, { useState, useRef, useMemo, useCallback } from "react";
import {
  CloudscapeJsonFormsStudio,
  type LlmClient,
  type SchemaClient,
  type LlmResponse,
  type SaveSchemaResult,
  type ListSchemasResult,
  type LoadSchemaResult,
  type SchemaListItem,
} from "@amzn/fin-tech-cloudscape-json-forms-studio";
import type { CloudscapeJsonSchema } from "@amzn/fin-tech-cloudscape-json-forms";
import { STUDIO_API_URLS, STUDIO_BROWSER_FOLDERS } from "./constants";
import { CUSTOM_RENDERERS, CUSTOM_FIELD_TYPES } from "./customRenderers";
import { getAuthenticationToken } from "src/auth/helpers";
import SaveSchemaModal from "./SaveSchemaModal";

/**
 * Returns the headers attached to every studio API call. The studio's
 * Cognito User Pools authorizer validates this token against the Nucleus
 * user pool. The dev proxy (setupProxy.js) discards the header on
 * localhost and replaces it with a SigV4 signature, so the value is only
 * meaningful in deployed environments — emitting it locally keeps the
 * request shape consistent.
 */
const getStudioApiHeaders = async (): Promise<Record<string, string>> => {
  const token = await getAuthenticationToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Build the LlmClient the studio expects. The studio provides an
 * AbortSignal which we forward to fetch so chat cancellation works.
 */
const createLlmClient = (): LlmClient => ({
  callLlm: async (request, signal): Promise<LlmResponse> => {
    const headers = await getStudioApiHeaders();
    const res = await fetch(STUDIO_API_URLS.llmUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...headers },
      body: JSON.stringify(request),
      signal,
    });
    if (!res.ok) {
      return {
        action: "message_only",
        message: `LLM request failed with status ${res.status}.`,
      };
    }
    return (await res.json()) as LlmResponse;
  },
  checkHealth: async (signal): Promise<boolean> => {
    try {
      const headers = await getStudioApiHeaders();
      const res = await fetch(STUDIO_API_URLS.healthUrl, {
        headers,
        signal,
      });
      return res.ok;
    } catch {
      return false;
    }
  },
});

/**
 * Build the SchemaClient the studio expects. Methods forward the optional
 * AbortSignal so the studio's SchemaBrowser can cancel in-flight list/get
 * requests on rapid folder switches.
 */
const createSchemaClient = (): SchemaClient => ({
  saveSchema: async (request, signal): Promise<SaveSchemaResult> => {
    try {
      const headers = await getStudioApiHeaders();
      const res = await fetch(STUDIO_API_URLS.saveUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...headers },
        body: JSON.stringify(request),
        signal,
      });
      const body = (await res
        .json()
        .catch(() => ({}))) as Partial<SaveSchemaResult>;
      if (!res.ok) {
        return {
          success: false,
          message: body.message ?? `Save failed with status ${res.status}.`,
        };
      }
      return {
        success: body.success ?? true,
        message: body.message ?? "Schema saved.",
        key: body.key,
      };
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") throw err;
      return {
        success: false,
        message: err instanceof Error ? err.message : "Unknown save error.",
      };
    }
  },
  // The studio service returns the list as a bare JSON array of
  // SchemaListItem objects; we wrap it in the ListSchemasResult envelope
  // the SchemaClient interface expects.
  listSchemas: async (folder, signal): Promise<ListSchemasResult> => {
    try {
      const headers = await getStudioApiHeaders();
      const url = folder
        ? `${STUDIO_API_URLS.listUrl}?folder=${encodeURIComponent(folder)}`
        : STUDIO_API_URLS.listUrl;
      const res = await fetch(url, { headers, signal });
      const body = await res.json().catch(() => null);
      if (!res.ok) {
        const message =
          body && typeof body === "object" && "message" in body
            ? String((body as { message?: unknown }).message)
            : `List failed with status ${res.status}.`;
        return { success: false, message, items: [] };
      }
      const items = Array.isArray(body) ? (body as SchemaListItem[]) : [];
      return { success: true, message: "Schemas listed.", items };
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") throw err;
      return {
        success: false,
        message: err instanceof Error ? err.message : "Unknown list error.",
        items: [],
      };
    }
  },
  // The service returns { name, folder, schema } directly (no success/message
  // envelope). We wrap it to match the LoadSchemaResult shape.
  getSchema: async (name, folder, signal): Promise<LoadSchemaResult> => {
    try {
      const headers = await getStudioApiHeaders();
      const qs = folder ? `?folder=${encodeURIComponent(folder)}` : "";
      const url = `${STUDIO_API_URLS.getUrl}/${encodeURIComponent(name)}${qs}`;
      const res = await fetch(url, { headers, signal });
      const body = (await res.json().catch(() => ({}))) as {
        message?: string;
        schema?: LoadSchemaResult["schema"];
        name?: string;
        folder?: string;
      };
      if (!res.ok) {
        return {
          success: false,
          message: body.message ?? `Get failed with status ${res.status}.`,
        };
      }
      return {
        success: true,
        message: "Schema loaded.",
        schema: body.schema,
        name: body.name,
        folder: body.folder,
      };
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") throw err;
      return {
        success: false,
        message: err instanceof Error ? err.message : "Unknown get error.",
      };
    }
  },
});

const FormStudioView: React.FC = () => {
  const [saveModalVisible, setSaveModalVisible] = useState(false);
  const schemaRef = useRef<CloudscapeJsonSchema>({
    type: "object",
    properties: {},
  });

  // Clients are stable for the page lifetime — useMemo keeps identity so
  // the studio doesn't unmount/remount its chat history on rerender.
  const llmClient = useMemo(() => createLlmClient(), []);
  const schemaClient = useMemo(() => createSchemaClient(), []);

  // Save handler owns all schema-persistence business logic. The modal is a
  // pure presentation component; it invokes this callback and renders the
  // returned result envelope.
  const handleSave = useCallback(
    async (input: { name: string; draft: boolean }) => {
      const schema = schemaRef.current;

      if (!schema.properties || Object.keys(schema.properties).length === 0) {
        return {
          success: false,
          message: "Cannot save an empty schema. Add at least one field.",
        };
      }

      const [draftRes, pendingRes] = await Promise.all([
        schemaClient.listSchemas("DRAFT"),
        schemaClient.listSchemas("PENDING_APPROVAL"),
      ]);
      if (!draftRes.success || !pendingRes.success) {
        return {
          success: false,
          message: "Failed to check for existing schemas.",
        };
      }
      const isDuplicate = [...draftRes.items, ...pendingRes.items].some(
        (item) => item.name.toLowerCase() === input.name.toLowerCase()
      );
      if (isDuplicate) {
        return {
          success: false,
          message: `A schema with the name "${input.name}" already exists in Draft or Pending Approval.`,
        };
      }

      const result = await schemaClient.saveSchema({
        name: input.name,
        schema,
        draft: input.draft,
      });
      return { success: result.success, message: result.message };
    },
    [schemaClient]
  );

  return (
    <div>
      <CloudscapeJsonFormsStudio
        llmClient={llmClient}
        schemaClient={schemaClient}
        browserFolders={STUDIO_BROWSER_FOLDERS}
        onSave={() => setSaveModalVisible(true)}
        persistKey="nucleus-form-studio"
        customFieldTypes={CUSTOM_FIELD_TYPES}
        customRenderers={CUSTOM_RENDERERS}
        onSchemaChange={(schema) => {
          schemaRef.current = schema;
        }}
      />
      <SaveSchemaModal
        visible={saveModalVisible}
        onDismiss={() => setSaveModalVisible(false)}
        onSave={handleSave}
      />
    </div>
  );
};

export default FormStudioView;
